import { useContext, useRef, useEffect } from 'react';
import { RadioGroup, Option, Button, InputError, Row, Col } from '@op/opds.opux';
import { IconAdd } from '@op/opds.opux-icons/action';
import message from '../../messages';
import AdditionalSignatoryForm from './AdditionalSignatoryForm';
import { AdditionalSignatoryData, additionalSignatoryTemplate, ContextWrapper } from '../../context/ContextWrapper';
import { deepCopy } from '../../util/Util';
import { id } from '../../Constants';
import UseWindowWidth from '../../hooks/useWindowWidth';

const AdditionalSignatory = () => {
    const { contractData, updateInformation } = useContext(ContextWrapper);
    const viewThreshold = 540;
    const width = UseWindowWidth();
    const value = contractData.additionalSignatorySelection;
    const checkValidation = contractData.checkValidation;
    const formRefs = useRef<(HTMLDivElement | null)[]>([]);
    const previousCount = useRef<number>(contractData.additionalSignatories.length);

    useEffect(() => {
        formRefs.current = formRefs.current.slice(0, contractData.additionalSignatories.length);
    }, [contractData.additionalSignatories.length]);

    useEffect(() => {
        const currentCount = contractData.additionalSignatories.length;
        const wasAdded = currentCount > previousCount.current;

        if (wasAdded && formRefs.current.length > 0) {
            const lastFormRef = formRefs.current[currentCount - 1];
            if (lastFormRef) {
                lastFormRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
                const firstInput = lastFormRef.querySelector('input');
                firstInput?.focus();
            }
        }

        previousCount.current = currentCount;
    }, [contractData.additionalSignatories.length]);

    const modifyState = (signatoryInfo: AdditionalSignatoryData, index: number) => {
        updateInformation((state: any) => {
            const copyOfState = deepCopy(state);
            copyOfState.additionalSignatories[index] = signatoryInfo;

            return {
                ...state,
                additionalSignatories: copyOfState.additionalSignatories,
            };
        });
    };

    const addSignatoryForm = () => {
        const copyOfContractData = deepCopy(contractData);
        const newSignatory: AdditionalSignatoryData = additionalSignatoryTemplate();
        copyOfContractData.additionalSignatories.push(newSignatory);
        updateInformation(copyOfContractData);
    };

    const removeForm = (index: number) => {
        const copyOfContractData = deepCopy(contractData);
        copyOfContractData.additionalSignatories.splice(index, 1);
        updateInformation(copyOfContractData);
    };

    return (
        <div className="opux-spacing-top-margin" data-testid="additional-signatory-section">
            <h3
                className="opux-spacing-top-margin-double
                            opux-spacing-bottom-margin-double opux-font-weight-demibold"
                data-testid="additional-signatory-title"
            >
                {message('additionalSignatoryHeading')}
            </h3>
            <RadioGroup
                id={id.ADDITIONAL_SIGNATORY_SELECTION}
                className="opux-spacing-bottom-margin-double"
                onChange={(_ev, val) => {
                    updateInformation((state: any) => {
                        const updated = { ...state, additionalSignatorySelection: val };
                        if (val === 'yes' && state.additionalSignatories.length === 0) {
                            updated.additionalSignatories = [additionalSignatoryTemplate()];
                        }
                        return updated;
                    });
                }}
                value={value}
                wrap
            >
                <Option value="yes">{message('yes')}</Option>
                <Option value="no">{message('no')}</Option>
            </RadioGroup>
            {checkValidation && value === '' && (
                <InputError
                    error={message('emptyValueErrorMessage')}
                    data-testid="additional-signatory-selection-error"
                />
            )}
            {value === 'yes' && (
                <>
                    {contractData.additionalSignatories.map((signatory: AdditionalSignatoryData, index: number) => (
                        <AdditionalSignatoryForm
                            key={signatory.id}
                            ref={(el) => (formRefs.current[index] = el)}
                            signatory={signatory}
                            updateInformation={(signatoryInfo: AdditionalSignatoryData) =>
                                modifyState(signatoryInfo, index)
                            }
                            enableDeleteButton={contractData.additionalSignatories.length > 1}
                            removeForm={() => removeForm(index)}
                        />
                    ))}
                    {width > viewThreshold ? (
                        <Row
                            center
                            className="opux-middle-sm opux-middle-xs opux-spacing-top-margin-double
                              opux-spacing-bottom-margin-double"
                            data-testid="addSignatoryRow"
                        >
                            <Col sm={6} xs={5}>
                                <h3 data-testid="addSignatoryText">{message('addSignatory')}</h3>
                            </Col>
                            <Col sm={6} xs={6} xsOffset={1} smOffset={0}>
                                <Button
                                    icon={IconAdd}
                                    iconLabel={message('addSignatory')}
                                    onClick={addSignatoryForm}
                                    disabled={contractData.additionalSignatories.length >= 5}
                                    data-testid="addAdditionalSignatoryButton"
                                />
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <h3
                                className="opux-align-center 
                                    opux-spacing-top-margin-double opux-spacing-bottom-margin-half"
                                data-testid="addSignatoryText"
                            >
                                {message('addSignatory')}
                            </h3>
                            <div
                                className="opux-align-center opux-spacing-bottom-margin-double"
                                data-testid="addSignatoryRow"
                            >
                                <Button
                                    icon={IconAdd}
                                    iconLabel={message('addSignatory')}
                                    minified
                                    onClick={addSignatoryForm}
                                    disabled={contractData.additionalSignatories.length >= 5}
                                    data-testid="addAdditionalSignatoryButton"
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default AdditionalSignatory;
